import {
  type ComputerUseAction,
  type ComputerUseAskBefore,
  type ComputerUseStatus,
  DEFAULT_COMPUTER_USE_ASK_BEFORE,
} from '@crowdex/agent-contracts/api';
import { AppWindow, Download, LoaderCircle, Trash2 } from 'lucide-react';
import { useCallback, useEffect, useState } from 'react';
import {
  SettingRow,
  SettingsGroup,
  SettingToggle,
  SettingValue,
  settingButtonClass,
} from '@/components/settings-page/setting-controls';
import { loadComputerUse, updateComputerUse } from '@/lib/computer-use';
import { errorMessage } from '@/lib/error-message';
import { settingsPreview } from '@/lib/settings-page/settings-preview';

const POLL_MS = 2_000;

/** What each switch does: messages are approved each time; the rest is asked by the agent. */
const ASK_BEFORE: readonly {
  key: keyof ComputerUseAskBefore;
  title: string;
  description: string;
}[] = [
  {
    key: 'passwords',
    title: 'Typing passwords',
    description: 'The agent is told to ask you first; Crowdex cannot see what it types',
  },
  {
    key: 'messages',
    title: 'Sending messages or emails',
    description: 'You approve each action in Mail, Messages, Slack and other messaging apps',
  },
  {
    key: 'purchases',
    title: 'Buying anything',
    description: 'The agent is told to ask you first; Crowdex cannot tell a purchase apart',
  },
];

/**
 * Settings › Computer use (D19b, desktop only): agents drive the Mac's apps through Cua Driver.
 * Its installation, the macOS permissions of CuaDriver, what agents ask before doing, and the
 * apps they may use without asking each time.
 */
export function ComputerUseSettingsSection() {
  const [status, setStatus] = useState<ComputerUseStatus | null>(null);
  const [pending, setPending] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const refresh = useCallback(async () => {
    try {
      setStatus(settingsPreview()?.computerUse ?? (await loadComputerUse()));
    } catch (cause) {
      setError(errorMessage(cause));
    }
  }, []);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  // Installing and granting go on in the background (granting waits for System Settings).
  const busy = status?.busy ?? null;
  useEffect(() => {
    if (busy === null) return;
    const poll = window.setInterval(() => void refresh(), POLL_MS);
    return () => window.clearInterval(poll);
  }, [busy, refresh]);

  async function act(action: ComputerUseAction): Promise<void> {
    setPending(true);
    setError(null);
    try {
      setStatus(await updateComputerUse(action));
    } catch (cause) {
      setError(errorMessage(cause));
    } finally {
      setPending(false);
    }
  }

  const failure = error ?? status?.error ?? null;
  const working = pending || busy !== null;
  if (status !== null && !status.supported)
    return <p className="text-muted-foreground text-sm">Computer use needs Crowdex on a Mac.</p>;
  const permissions = status?.permissions ?? null;
  const askBefore = status?.askBefore ?? DEFAULT_COMPUTER_USE_ASK_BEFORE;
  const grant = (
    <button
      className={settingButtonClass()}
      disabled={working}
      onClick={() => void act({ action: 'grant' })}
      type="button"
    >
      {busy === 'granting' && <LoaderCircle className="size-3.5 animate-spin" />}
      {busy === 'granting' ? 'Waiting for macOS…' : 'Open System Settings'}
    </button>
  );

  return (
    <>
      <SettingsGroup title="This Mac">
        <SettingRow
          description="In the background, through Cua Driver"
          title="Let agents use the apps of this Mac"
        >
          <SettingToggle
            checked={status?.enabled ?? false}
            label="Let agents use the apps of this Mac"
            onChange={(enabled) => void act({ action: 'enable', enabled })}
          />
        </SettingRow>
        <SettingRow
          description={
            status?.installed
              ? `Version ${status.installed}, in /Applications/CuaDriver.app (by Cua, MIT)`
              : 'Installed with your approval the first time an agent needs it (about 80 MB)'
          }
          title="Cua Driver"
        >
          {status?.installed ? (
            <button
              className={settingButtonClass()}
              disabled={working}
              onClick={() => void act({ action: 'uninstall' })}
              type="button"
            >
              {busy === 'uninstalling' ? (
                <LoaderCircle className="size-3.5 animate-spin" />
              ) : (
                <Trash2 className="size-3.5" />
              )}
              Uninstall
            </button>
          ) : (
            <button
              className={settingButtonClass()}
              disabled={working || status === null}
              onClick={() => void act({ action: 'install' })}
              type="button"
            >
              {busy === 'installing' ? (
                <LoaderCircle className="size-3.5 animate-spin" />
              ) : (
                <Download className="size-3.5" />
              )}
              {busy === 'installing' ? 'Installing…' : 'Install'}
            </button>
          )}
        </SettingRow>
        {status?.installed && (
          <>
            <SettingRow
              description="Lets Cua Driver see the apps' windows"
              title="Screen recording"
            >
              {permissions?.screenRecording ? (
                <SettingValue tone="success">Allowed</SettingValue>
              ) : (
                grant
              )}
            </SettingRow>
            <SettingRow description="Needed to click and type" title="Accessibility">
              {permissions?.accessibility ? (
                <SettingValue tone="success">Allowed</SettingValue>
              ) : (
                grant
              )}
            </SettingRow>
          </>
        )}
        {failure && <p className="px-4 pb-3 text-destructive text-xs">{failure}</p>}
      </SettingsGroup>
      <SettingsGroup title="Ask before">
        {ASK_BEFORE.map((item) => (
          <SettingRow description={item.description} key={item.key} title={item.title}>
            <SettingToggle
              checked={askBefore[item.key]}
              disabled={status === null}
              label={`Ask before ${item.title.toLowerCase()}`}
              onChange={(value) =>
                void act({ action: 'ask-before', askBefore: { [item.key]: value } })
              }
            />
          </SettingRow>
        ))}
      </SettingsGroup>
      <SettingsGroup title="Allowed apps">
        {(status?.apps ?? []).length === 0 ? (
          <p className="px-4 py-3.5 text-muted-foreground text-sm">
            None yet. When an agent first uses an app, its approval offers to always allow it.
          </p>
        ) : (
          status?.apps.map((app) => (
            <SettingRow
              description={`Allowed ${new Date(app.allowedAt).toLocaleDateString()}`}
              key={app.bundleId}
              leading={
                app.icon === null ? (
                  <span className="flex size-8 items-center justify-center rounded-lg bg-control text-muted-foreground">
                    <AppWindow className="size-4" />
                  </span>
                ) : (
                  <img alt="" className="size-8" src={app.icon} />
                )
              }
              title={app.name}
            >
              <button
                className={settingButtonClass()}
                disabled={pending}
                onClick={() => void act({ action: 'revoke', bundleId: app.bundleId })}
                type="button"
              >
                Revoke
              </button>
            </SettingRow>
          ))
        )}
      </SettingsGroup>
    </>
  );
}
